import { useEffect, useState } from "react";
import { Box, Button } from "@mui/material";
import { Icon } from "../mui";
import { brainTokens } from "../design/brainTheme";
import { presignFile, getObjectText } from "../lib/brain-api";
import { MarkdownDocument } from "./MarkdownDocument";
import { SandboxedDocViewer, resolveDocHref } from "./SandboxedDocViewer";

const c = brainTokens.color;

type Kind = "markdown" | "html" | "image" | "pdf" | "text" | "other";

function kindFor(name: string): Kind {
  const ext = name.split(".").pop()?.toLowerCase() ?? "";
  if (["md", "markdown", "mdx"].includes(ext)) return "markdown";
  if (["html", "htm"].includes(ext)) return "html";
  if (["png", "jpg", "jpeg", "gif", "svg", "webp", "avif", "bmp"].includes(ext)) return "image";
  if (ext === "pdf") return "pdf";
  if (["txt", "log", "json", "yaml", "yml", "csv", "sql", "sh", "ts", "js", "py"].includes(ext)) return "text";
  return "other";
}

/**
 * Renders a stored file inline. Markdown via the shared MarkdownDocument, HTML in a
 * script-less sandboxed iframe (same isolation as the org chart), images/PDF native,
 * text in a mono pre. Anything else falls back to open-raw. Reusable across the project
 * Files tab and the Library.
 *
 * Relative links in stored HTML resolve via onNavigate (the caller swaps the viewed
 * file) — same mechanism as the Docs tab.
 */
export function FileViewer({
  fileKey,
  name,
  onBack,
  onNavigate,
}: {
  fileKey: string;
  name: string;
  onBack?: () => void;
  /** Called with the RESOLVED store key when a relative link inside HTML is clicked. */
  onNavigate?: (absoluteKey: string) => void;
}) {
  const kind = kindFor(name);
  const [url, setUrl] = useState<string | null>(null);
  const [text, setText] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    setUrl(null);
    setText(null);
    setError(null);
    (async () => {
      try {
        if (kind === "markdown" || kind === "text" || kind === "html") {
          const t = await getObjectText(fileKey);
          if (!cancelled) setText(t);
        } else {
          const u = await presignFile(fileKey);
          if (!cancelled) setUrl(u);
        }
      } catch {
        if (!cancelled) setError("Could not load this file.");
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [fileKey, kind]);

  const openRaw = async () => {
    try {
      window.open(await presignFile(fileKey), "_blank", "noopener");
    } catch {
      setError("Could not open this file.");
    }
  };

  return (
    <Box>
      <Box sx={{ display: "flex", alignItems: "center", gap: 1.5, mb: 2, flexWrap: "wrap", minWidth: 0 }}>
        {onBack && (
          <Button size="small" startIcon={<Icon name="arrow-left" size={14} />} onClick={onBack}>
            Back to files
          </Button>
        )}
        <Box sx={{ fontFamily: brainTokens.font.mono, fontSize: 13, color: c.ink2, flex: 1, minWidth: 0, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
          {name}
        </Box>
        <Button size="small" variant="text" endIcon={<Icon name="external-link" size={13} />} onClick={() => void openRaw()}>
          Open raw
        </Button>
      </Box>

      {error ? (
        <Box sx={{ fontSize: 13, color: c.dangerText }}>{error}</Box>
      ) : kind === "markdown" && text != null ? (
        <MarkdownDocument content={text || "_Empty document._"} documentKey={fileKey} projectId={fileKey.startsWith('projects/') ? fileKey.split('/')[1] : undefined} onOpenFile={onNavigate}/>
      ) : kind === "text" && text != null ? (
        <Box
          component="pre"
          sx={{
            fontFamily: brainTokens.font.mono,
            fontSize: 12.5,
            lineHeight: 1.6,
            color: c.ink3,
            background: c.panelBg2,
            border: `1px solid ${c.borderLight}`,
            borderRadius: "8px",
            p: 2,
            overflowX: "auto",
            whiteSpace: "pre-wrap",
            wordBreak: "break-word",
          }}
        >
          {text}
        </Box>
      ) : kind === "html" && text != null ? (
        <SandboxedDocViewer
          html={text}
          title={name}
          height="70vh"
          onNavigate={
            onNavigate
              ? (href) => {
                  const abs = resolveDocHref(fileKey, href);
                  if (abs) onNavigate(abs);
                }
              : undefined
          }
        />
      ) : kind === "image" && url ? (
        <Box sx={{ textAlign: "center" }}>
          <Box component="img" src={url} alt={name} sx={{ maxWidth: "100%", height: "auto", borderRadius: "8px", border: `1px solid ${c.borderLight}` }} />
        </Box>
      ) : kind === "pdf" && url ? (
        <Box component="iframe" src={url} title={name} sx={{ width: "100%", height: "80vh", minHeight: 500, border: `1px solid ${c.border}`, borderRadius: "8px" }} />
      ) : kind === "other" ? (
        <Box sx={{ fontSize: 13.5, color: c.muted2 }}>
          No inline preview for this file type.{" "}
          <Box component="span" sx={{ color: c.tealInk, cursor: "pointer" }} onClick={() => void openRaw()}>
            Open raw ↗
          </Box>
        </Box>
      ) : (
        <Box sx={{ fontSize: 13, color: c.muted2 }}>Loading…</Box>
      )}
    </Box>
  );
}
